import { Link, useRouterState } from "@tanstack/react-router";
import { useI18n } from "~/i18n";
import { useSession } from "~/lib/access/session-context";
import type { RuleSearch } from "~/lib/url/search";
import { lookupContext } from "~/routes/rules/lookup-context";
import { RULES_VERIFICATION_PATH, isPlatformAdmin } from "./nav-items";
import { WorkspaceTabs } from "./workspace-tabs";
import "./rules-workspace-nav.css";

export type RulesSection =
  | "lookup"
  /** @deprecated Catálogo NCM is "Pesquisa por produto" inside Consultar; `/catalog/ncm` redirects. */
  | "catalog"
  | "library"
  | "new"
  | "coverage"
  | "gaps"
  | "verification"
  /** The guide is reached from the "?" help link, not a tab. */
  | "guide";

/**
 * Regras tabs: Consultar · Biblioteca · Novas regras · Cobertura · Lacunas ·
 * Verificação, with the matrix guide behind a "?" help link at the end of the
 * strip.
 */
export function RulesWorkspaceNav({ active }: { active: RulesSection }) {
  const { t } = useI18n();
  const { can, effectiveRole } = useSession();
  const search = useRouterState({ select: (state) => state.location.search }) as RuleSearch;
  const context = lookupContext(search);
  return (
    <div className="rules-workspace-nav">
      <WorkspaceTabs
        label={t("lookup.destinations")}
        active={active === "catalog" ? "lookup" : active}
        items={[
          { id: "lookup", to: "/rules", label: t("lookup.title"), search: context },
          {
            id: "library",
            to: "/rules/library",
            label: t("lookup.library"),
            search: { ...context, kind: "FISCAL" },
          },
          { id: "new", to: "/rules/new", label: t("rules.new.tab") },
          { id: "coverage", to: "/rules/coverage", label: t("coverage.tab") },
          { id: "gaps", to: "/rules/gaps", label: t("gaps.tab") },
          // Admin-only, matching the Regras → Verificação entry in the sidebar.
          ...(isPlatformAdmin(can, effectiveRole)
            ? [{ id: "verification", to: RULES_VERIFICATION_PATH, label: t("verification.tab") }]
            : []),
        ]}
      />
      <Link
        to="/rules/guide"
        search={context}
        className="rules-workspace-nav__help"
        aria-current={active === "guide" ? "page" : undefined}
        aria-label={t("rules.guide.link")}
        title={t("rules.guide.link")}
      >
        <span className="rules-workspace-nav__help-mark" aria-hidden="true">
          ?
        </span>
        <span className="rules-workspace-nav__help-text" aria-hidden="true">
          {t("rules.help.short")}
        </span>
      </Link>
    </div>
  );
}
